import React, { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert, Autocomplete, TextField } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { romaneioService } from '../../services/romaneio';

interface RelatorioRomaneioBaseProps {
  titulo: string;
  gerarRelatorio: (idRomaneio: number) => Promise<Blob>;
  getFilename: (idRomaneio: number) => string;
}

const RelatorioRomaneioBase: React.FC<RelatorioRomaneioBaseProps> = ({ titulo, gerarRelatorio, getFilename }) => {
  const navigate = useNavigate();
  const [idRomaneio, setIdRomaneio] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'error' });

  const { data: romaneios = [] } = useQuery({
    queryKey: ['romaneios'],
    queryFn: romaneioService.getAll,
  });

  const romaneioSelecionado = romaneios.find((r) => Number(r.id) === idRomaneio) || null;

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleGerar = async () => {
    if (!idRomaneio) {
      setSnackbar({ open: true, message: 'Selecione o romaneio.', severity: 'error' });
      return;
    }
    setLoading(true);
    setSnackbar({ open: true, message: 'Gerando arquivo para impressão. Aguarde!', severity: 'info' });
    try {
      const blob = await gerarRelatorio(idRomaneio);
      downloadBlob(blob, getFilename(idRomaneio));
      setSnackbar({ open: true, message: 'Relatório gerado com sucesso.', severity: 'info' });
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Erro ao gerar relatório: ' + (error.message || error), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/Relatorios');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {titulo}
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleVoltar}>
          Voltar
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
        <Autocomplete
          options={romaneios}
          getOptionLabel={(r) => `#${r.id} - ${r.nomePromotor || ''} - ${r.nomePraca || ''}`}
          value={romaneioSelecionado}
          onChange={(_, value) => setIdRomaneio(value ? Number(value.id) : null)}
          renderInput={(params) => <TextField {...params} label="Romaneio" placeholder="Selecione o romaneio" />}
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleGerar} disabled={loading}>
            Gerar relatório
          </Button>
          <Button variant="outlined" onClick={handleVoltar} disabled={loading}>
            Voltar
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RelatorioRomaneioBase;
